// src/store/userStore.ts
// Current time: Wednesday, August 13, 2025 at 11:40:00 AM UTC
/**
 * .tuMetrics {"tu1": 0.80, "tu2": 0.88, "tu3": 8, "notes": "User store — auth, session, WebSocket presence, profile data", "tuVersion": "2.5"}
 * .help "Manages user authentication, session, presence, and profile data."
 */
import { create } from 'zustand';
import { User, AccessRole, ActivityRole, VideoDisplayMode } from '../types';
import { supabase, initializeUser } from '../lib/supabase';
import useCardStore from './cardStore';
// August 13, 2025 8:45 PM: Removed direct import of useSprintStore to break circular dependency
import useRoadmapStore from './roadmapStore';
import useTeamStore from './teamStore';
import useCommentStore from '././commentStore';
import useFollowStore from '././followStore';
import useRequirementStore from './requirementStore';
import useFeedStore from './feedStore';
import usePlanStore from './planStore';
import useSharedItemStore from './sharedItemStore'; // December 9, 2025: Initialize on app load
import type { RealtimeChannel } from '@supabase/supabase-js';
import { Session } from '@supabase/supabase-js'; // Import Session type
import { composeCurrentUser, type MembershipSnapshot } from './user/composeCurrentUser';
import { authBootstrapDecision, clearedAuthSlice, shouldRedirectToLogin } from './user/authDecision';
import { hasReportsAccess, isAdminRole, isManagerRole, isOwnerRole } from './user/roles';
import { unsubscribeThisDevicePush } from './user/unsubscribePush';

interface UserState {
  currentUser: User | null;
  allUsers: User[];
  isLoadingUsers: boolean;
  coreDataReady: boolean;
  isDarkMode: boolean;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  isTokenRefreshing: boolean;
  playingBackgroundVideo: boolean;
  backgroundVideoUrl: string | null;
  backgroundVideoDisplayMode: VideoDisplayMode;
  hasInitializedAuth: boolean; // August 13, 2025 11:40 AM: New state to track initial auth completion
  _usersRealtimeChannel: RealtimeChannel | null;
  _presenceChannel: RealtimeChannel | null;
  _presenceChannelStatus: 'idle' | 'joining' | 'subscribed' | 'error' | 'closed'; // Track channel subscription status
  onlineUserIds: Set<string>; // Set of currently online user IDs (instant updates!)
  presenceTick: number; // Incremented on presence changes to trigger re-renders
  _presenceReconnectTimeout: NodeJS.Timeout | null; // Track reconnection timeout
  _presenceReconnectAttempts: number; // Track reconnection attempts
  _isReconnecting: boolean; // Prevent multiple simultaneous reconnection attempts

  setCurrentUser: (user: User | null) => void; // Allow null for clearing user
  toggleFlowState: () => Promise<void>;
  toggleDarkMode: () => Promise<void>;
  updateUserProfile: (data: {
    name?: string;
    firstName?: string;
    lastName?: string;
    avatar?: string;
    enableBackgroundAnimation?: boolean;
    animationIntensity?: number;
    backgroundType?: string;
    warpSpeed?: number;
    headlineFeedBundle?: string | null;
    headlineFeedKeyword?: string | null;
    agentInstructions?: string;
    agentCurrentPeriodTokensUsed?: number;
    agentTokenLimitPerPeriod?: number;
    agentLastTokenResetDate?: string;
    agentLastTokenWarningSent?: string;
    agentUserObservations?: Record<string, unknown>;
    agentCapabilities?: Record<string, boolean>;
    casualName?: string;
    bio?: string;
    skills?: string[];
    funFact?: string;
    pronouns?: string;
    timezone?: string;
    workCity?: string | null;
    workLat?: number | null;
    workLng?: number | null;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  /** JWT gone / refresh failed: drop realtime so we never reconnect as anon. */
  sessionLost: (opts?: { redirect?: boolean }) => Promise<void>;
  initializeAuth: () => Promise<{ dbUser: User | null }>;
  updateCurrentUserFromSession: (session: Session) => Promise<void>;
  fetchAllUsers: (companyId?: string | null) => Promise<void>;
  setupUsersRealtimeSubscription: () => Promise<void>;
  cleanupUsersRealtimeSubscription: () => void;
  startBackgroundVideo: (url: string, mode: VideoDisplayMode) => void;
  stopBackgroundVideo: () => void;
  toggleBackgroundVideoMode: () => void;
  
  // Helper functions for role checking
  isCurrentUserAdmin: () => boolean;
  isCurrentUserOwner: () => boolean;
  isCurrentUserManager: () => boolean;
  isCurrentUserReportsAccess: () => boolean;
  
  // December 5, 2025: WEBSOCKET PRESENCE - The ONE source of truth
  // Use this everywhere instead of local isUserOnline calculations!
  isUserOnline: (userId: string) => boolean;
  setupPresenceChannel: () => Promise<void>;
  cleanupPresenceChannel: () => void;
}

// Load dark mode preference from localStorage on initialization
// Note: Database preference will override this once user is loaded
const getInitialDarkMode = (): boolean => {
  try {
    const saved = localStorage.getItem('isDarkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn('Error loading dark mode preference from localStorage:', error);
  }
  return true; // Default to dark mode
};

// December 5, 2025: WEBSOCKET PRESENCE - Lean & Mean
// "There can be only one (presence dot system)." - Fortune Cookie #14
const PRESENCE_CHANNEL_NAME = 'presence:online'; // Single global channel

const useUserStore = create<UserState>((set, get) => ({
  currentUser: null,
  allUsers: [],
  isLoadingUsers: false,
  coreDataReady: false,
  isDarkMode: getInitialDarkMode(),
  isAuthenticated: false,
  isLoadingAuth: true,
  isTokenRefreshing: false,
  playingBackgroundVideo: false,
  backgroundVideoUrl: null,
  backgroundVideoDisplayMode: 'fullscreen',
  hasInitializedAuth: false,
  _usersRealtimeChannel: null,
  
  // December 5, 2025: WebSocket Presence State
  _presenceChannel: null,
  _presenceChannelStatus: 'idle' as 'idle' | 'joining' | 'subscribed' | 'error' | 'closed',
  onlineUserIds: new Set<string>(),
  presenceTick: 0, // Incremented on presence events to trigger re-renders
  _presenceReconnectTimeout: null as NodeJS.Timeout | null, // Track reconnection timeout
  _presenceReconnectAttempts: 0, // Track reconnection attempts
  _isReconnecting: false, // Prevent multiple simultaneous reconnection attempts

  setCurrentUser: (user) => {
    set({
      currentUser: user,
      isAuthenticated: !!user,
      isLoadingAuth: false,
      coreDataReady: user ? get().coreDataReady : false,
    });
    
    // Update dark mode preference from database if user is loaded
    // Always use database value if user is loaded (even if it's the default)
    // This ensures cross-device sync works correctly (especially in incognito mode)
    if (user) {
      // Always use database value when user is loaded (database is source of truth)
      // The database value is already set with defaults in initializeUser()
      set({ isDarkMode: user.isDarkMode ?? true });
      // Also update localStorage to keep it in sync
      try {
        localStorage.setItem('isDarkMode', JSON.stringify(user.isDarkMode ?? true));
      } catch (error) {
        console.warn('Error syncing dark mode preference to localStorage:', error);
      }
    }
    
    console.log(
      'userStore: setCurrentUser - isAuthenticated:',
      get().isAuthenticated,
      'isLoadingAuth:',
      get().isLoadingAuth
    );
  },

  updateCurrentUserFromSession: async (session) => {
    set({ isTokenRefreshing: true });

    try {
      const { dbUser: rawDbUser } = await initializeUser();

      if (!rawDbUser) {
        set({ isTokenRefreshing: false, currentUser: null, isAuthenticated: false });
        return;
      }

      let stableCompanyId: string | null = rawDbUser.companyId ?? null;
      let membershipRow: MembershipSnapshot | null = null;

      try {
        const { data, error: membershipErr } = await supabase
          .from('memberships')
          .select(`
            company_id, 
            invite_accepted, 
            is_guest, 
            guest_access_level,
            companies (
              domain
            )
          `)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (membershipErr) {
          console.warn('userStore: updateCurrentUserFromSession - membership lookup warning:', membershipErr);
        } else if (data) {
          membershipRow = data as MembershipSnapshot;
          // Membership is source of truth; users table has no company_id.
          stableCompanyId = membershipRow.company_id ?? stableCompanyId;
        }
      } catch (e) {
        console.warn('userStore: updateCurrentUserFromSession - membership lookup threw:', e);
      }

      const currentUser = composeCurrentUser({
        raw: { ...rawDbUser, companyId: stableCompanyId ?? rawDbUser.companyId ?? null },
        sessionEmail: session.user?.email,
        membership: membershipRow,
      });

      set({
        currentUser,
        isAuthenticated: true,
        isTokenRefreshing: false,
      });
      console.log('userStore: updateCurrentUserFromSession - User state updated.');

      if (currentUser.companyId && currentUser.inviteAccepted) {
        await get().fetchAllUsers(currentUser.companyId);
        console.log('userStore: updateCurrentUserFromSession - All users fetched.');
      }

    } catch (error) {
      console.error('userStore: updateCurrentUserFromSession - Error updating user data:', error);
      set({
        isTokenRefreshing: false,
        currentUser: null,
        isAuthenticated: false,
      });
    }
  },

  toggleFlowState: async () => {
    const state = get();
    if (!state.currentUser) return;

    const newFlowState = !state.currentUser.inFlowState;

    try {
      // Update database
      const { error } = await supabase
        .from('users')
        .update({ in_flow_state: newFlowState })
        .eq('id', state.currentUser.id);

      if (error) throw error;

      // Update local state
      set((s) => ({
        currentUser: s.currentUser ? { ...s.currentUser, inFlowState: newFlowState } : null,
      }));

      // Real-Time Schedule: Create or end live activity
      // Dynamic import to avoid circular dependency
      const { startLiveActivity, endCurrentActivity } = await import('../services/liveActivityService');
      
      if (newFlowState) {
        // Entering Flow mode - create live activity
        const result = await startLiveActivity('flow', {
          title: 'Flow Mode',
          description: 'Deep focus session',
        });
        
        if (!result.success) {
          console.warn('Failed to create Flow mode live activity:', result.error);
        } else {
          console.log('✅ Flow mode live activity started:', result.activityId);
        }
      } else {
        // Exiting Flow mode - end live activity
        const result = await endCurrentActivity('flow');
        
        if (!result.success) {
          console.warn('Failed to end Flow mode live activity:', result.error);
        } else {
          console.log('✅ Flow mode live activity ended');
        }
      }

      const companyId = get().currentUser?.companyId;
      await get().fetchAllUsers(companyId);
    } catch (error) {
      console.error('Error toggling flow state:', error);
      set((s) => ({
        currentUser: s.currentUser ? { ...s.currentUser, inFlowState: !newFlowState } : null,
      }));
    }
  },

  toggleDarkMode: async () => {
    const state = get();
    const newDarkMode = !state.isDarkMode;
    
    // Update local state immediately
    set({ isDarkMode: newDarkMode });
    
    // Save to localStorage for immediate persistence
    try {
      localStorage.setItem('isDarkMode', JSON.stringify(newDarkMode));
    } catch (error) {
      console.warn('Error saving dark mode preference to localStorage:', error);
    }
    
    // Save to database for cross-device sync
    if (state.currentUser) {
      try {
        const { error } = await supabase
          .from('users')
          .update({ is_dark_mode: newDarkMode })
          .eq('id', state.currentUser.id);
        
        if (error) {
          console.warn('Error saving dark mode preference to database (column may not exist yet):', error);
        }
      } catch (error) {
        console.warn('Error saving dark mode preference to database (column may not exist yet):', error);
      }
    }
  },

  updateUserProfile: async (data) => {
    const state = get();
    if (!state.currentUser) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: data.name,
          first_name: data.firstName,
          last_name: data.lastName,
          ...(data.avatar ? { avatar_url: data.avatar } : {}),
          ...(data.enableBackgroundAnimation !== undefined
            ? { enable_background_animation: data.enableBackgroundAnimation }
            : {}),
          ...(data.animationIntensity !== undefined
            ? { animation_intensity: data.animationIntensity }
            : {}),
          ...(data.backgroundType !== undefined
            ? { background_type: data.backgroundType }
            : {}),
          ...(data.warpSpeed !== undefined
            ? { warp_speed: data.warpSpeed }
            : {}),
          ...(data.headlineFeedBundle !== undefined
            ? { headline_feed_bundle: data.headlineFeedBundle }
            : {}),
          ...(data.headlineFeedKeyword !== undefined
            ? { headline_feed_keyword: data.headlineFeedKeyword }
            : {}),
          ...(data.agentInstructions !== undefined ? { agent_instructions: data.agentInstructions } : {}),
          ...(data.agentCurrentPeriodTokensUsed !== undefined
            ? { agent_current_period_tokens_used: data.agentCurrentPeriodTokensUsed }
            : {}),
          ...(data.agentTokenLimitPerPeriod !== undefined
            ? { agent_token_limit_per_period: data.agentTokenLimitPerPeriod }
            : {}),
          ...(data.agentLastTokenResetDate !== undefined
            ? { agent_last_token_reset_date: data.agentLastTokenResetDate }
            : {}),
          ...(data.agentLastTokenWarningSent !== undefined
            ? { agent_last_token_warning_sent: data.agentLastTokenWarningSent }
            : {}),
          ...(data.agentUserObservations !== undefined ? { agent_user_observations: data.agentUserObservations } : {}),
          ...(data.agentCapabilities !== undefined ? { agent_capabilities: data.agentCapabilities } : {}),
          ...(data.casualName !== undefined ? { casual_name: data.casualName } : {}),
          ...(data.bio !== undefined ? { bio: data.bio } : {}),
          ...(data.skills !== undefined ? { skills: data.skills } : {}),
          ...(data.funFact !== undefined ? { fun_fact: data.funFact } : {}),
          ...(data.pronouns !== undefined ? { pronouns: data.pronouns } : {}),
          ...(data.timezone !== undefined ? { timezone: data.timezone } : {}),
          ...(data.workCity !== undefined ? { work_city: data.workCity } : {}),
          ...(data.workLat !== undefined ? { work_lat: data.workLat } : {}),
          ...(data.workLng !== undefined ? { work_lng: data.workLng } : {}),
        })
        .eq('id', state.currentUser.id);

      if (error) throw error;

      set((s) => ({
        currentUser: s.currentUser
          ? {
              ...s.currentUser,
              name: data.name || s.currentUser.name,
              firstName: data.firstName || s.currentUser.firstName,
              lastName: data.lastName || s.currentUser.lastName,
              timezone: data.timezone !== undefined ? data.timezone : s.currentUser.timezone,
              workCity: data.workCity !== undefined ? data.workCity : s.currentUser.workCity,
              workLat: data.workLat !== undefined ? data.workLat : s.currentUser.workLat,
              workLng: data.workLng !== undefined ? data.workLng : s.currentUser.workLng,
              ...(data.avatar ? { avatar: data.avatar } : {}),
              ...(data.enableBackgroundAnimation !== undefined
                ? { enableBackgroundAnimation: data.enableBackgroundAnimation }
                : {}),
              ...(data.animationIntensity !== undefined ? { animationIntensity: data.animationIntensity } : {}),
              ...(data.backgroundType !== undefined ? { backgroundType: data.backgroundType } : {}),
              ...(data.warpSpeed !== undefined ? { warpSpeed: data.warpSpeed } : {}),
              ...(data.headlineFeedBundle !== undefined
                ? { headlineFeedBundle: data.headlineFeedBundle }
                : {}),
              ...(data.headlineFeedKeyword !== undefined
                ? { headlineFeedKeyword: data.headlineFeedKeyword }
                : {}),
              ...(data.agentInstructions !== undefined ? { agentInstructions: data.agentInstructions } : {}),
              ...(data.agentCurrentPeriodTokensUsed !== undefined
                ? { agentCurrentPeriodTokensUsed: data.agentCurrentPeriodTokensUsed }
                : {}),
              ...(data.agentTokenLimitPerPeriod !== undefined
                ? { agentTokenLimitPerPeriod: data.agentTokenLimitPerPeriod }
                : {}),
              ...(data.agentLastTokenResetDate !== undefined
                ? { agentLastTokenResetDate: data.agentLastTokenResetDate }
                : {}),
              ...(data.agentLastTokenWarningSent !== undefined
                ? { agentLastTokenWarningSent: data.agentLastTokenWarningSent }
                : {}),
              ...(data.agentUserObservations !== undefined
                ? { agentUserObservations: data.agentUserObservations }
                : {}),
              ...(data.agentCapabilities !== undefined ? { agentCapabilities: data.agentCapabilities }
                : {}),
              ...(data.casualName !== undefined ? { casualName: data.casualName } : {}),
              ...(data.bio !== undefined ? { bio: data.bio } : {}),
              ...(data.skills !== undefined ? { skills: data.skills } : {}),
              ...(data.funFact !== undefined ? { funFact: data.funFact } : {}),
              ...(data.pronouns !== undefined ? { pronouns: data.pronouns } : {}),
            }
          : null,
      }));
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  fetchAllUsers: async (companyId?: string | null) => {
    console.log('🔍 fetchAllUsers called', new Error().stack);
    if (get().isLoadingUsers) {
      console.log('⏭️ fetchAllUsers skipped: already loading');
      return;
    }
    set({ isLoadingUsers: true });
    try {
      if (!companyId) {
        set({ allUsers: [] });
        return;
      }

      const { data: membershipsData, error: membershipsError } = await supabase
        .from('memberships')
        .select(`
          user_id,
          access_role,
          activity_roles,
          reports_team_id,
          team_id,
          company_id,
          is_guest,
          guest_access_level,
          deactivated_at
        `)
        .eq('company_id', companyId)
        .eq('invite_accepted', true) // CRITICAL: Only fetch accepted memberships
        .is('deactivated_at', null) // December 5, 2025: Hide deactivated users from everyone
        .order('created_at', { ascending: false });

      if (membershipsError) throw membershipsError;

      const userIds = membershipsData.map((m) => m.user_id);

      const { data: usersProfileData, error: usersProfileError } = await supabase
        .from('users')
        .select(`
          id, email, name, first_name, last_name, avatar_url,
          in_flow_state, current_activity_type, current_focus_label, enable_background_animation, animation_intensity,
          background_type, warp_speed, headline_feed_bundle, headline_feed_keyword,
          backplane_pin_enabled, backplane_desk_cleared,
          is_agent, agent_type, llm_model, agent_instructions,
          agent_current_period_tokens_used, agent_token_limit_per_period,
          agent_last_token_reset_date, agent_last_token_warning_sent,
          agent_user_observations, agent_capabilities, casual_name,
          bio, skills, fun_fact, pronouns,
          timezone, work_city, work_lat, work_lng, office_status, last_seen_at,
          external_agent_source, teambots_agent_id, teambots_org_id,
          teambots_job_title, teambots_job_function, teambots_department,
          teambots_profile_name, teambots_starter_profile_slug, teambots_organization_name,
          teambots_default_brain_id, teambots_default_brain_display_name, teambots_gateway_mode,
          teambots_runtime_config_version, teambots_manifest_synced_at,
          teambots_desktop_session_url, teambots_partner_agent_routing_mode,
          bio
        `)
        .in(
          'id',
          userIds.length
            ? userIds
            : ['00000000-0000-0000-0000-000000000000']
        )
        .order('name');

      if (usersProfileError) throw usersProfileError;

      const usersProfileMap = new Map(usersProfileData.map((u) => [u.id, u]));

      // Fetch private team memberships for all users
      const { data: privateTeamMemberships, error: privateTeamError } = await supabase
        .from('private_team_memberships')
        .select('user_id, team_id')
        .in('user_id', userIds);

      if (privateTeamError) {
        console.warn('Error fetching private team memberships:', privateTeamError);
      }

      // Create a map of user_id -> array of private team IDs
      const privateTeamsMap = new Map<string, string[]>();
      (privateTeamMemberships ?? []).forEach((ptm) => {
        if (!privateTeamsMap.has(ptm.user_id)) {
          privateTeamsMap.set(ptm.user_id, []);
        }
        privateTeamsMap.get(ptm.user_id)!.push(ptm.team_id);
      });

      const combinedUsers: User[] = Array.from(usersProfileMap.values()).map((u) => {
        const membership = membershipsData.find((m) => m.user_id === u.id);
        return {
          id: u.id,
          email: u.email || '', // CRITICAL: Include email for calendar user filtering
          name: u.name || u.email?.split('@')[0] || 'Anonymous',
          firstName: u.first_name ?? null,
          lastName:  u.last_name ?? null,
          avatar:    u.avatar_url || '',
          accessRole: (membership?.access_role ?? 'viewer') as AccessRole,
          activityRoles: (membership?.activity_roles ?? []) as ActivityRole[],
          reportsTeamId: membership?.reports_team_id ?? null,
          inFlowState: u.in_flow_state,
          currentActivityType: u.current_activity_type ?? null,
          currentFocusLabel: (u as { current_focus_label?: string | null }).current_focus_label ?? null,
          team: membership?.team_id ?? null,
          privateTeams: privateTeamsMap.get(u.id) || [],
          companyId: membership?.company_id ?? companyId ?? null,
          enableBackgroundAnimation: u.enable_background_animation || false,
          animationIntensity: u.animation_intensity || 0.2,
          backgroundType: u.background_type || 'network',
          warpSpeed: u.warp_speed || 5,
          headlineFeedBundle: (u as { headline_feed_bundle?: string | null }).headline_feed_bundle || 'mixed',
          headlineFeedKeyword: (u as { headline_feed_keyword?: string | null }).headline_feed_keyword || null,
          backplanePinEnabled: !!(u as { backplane_pin_enabled?: boolean }).backplane_pin_enabled,
          backplaneDeskCleared: !!(u as { backplane_desk_cleared?: boolean }).backplane_desk_cleared,
          workCity: (u as { work_city?: string | null }).work_city ?? null,
          workLat: typeof (u as { work_lat?: number | null }).work_lat === 'number'
            ? (u as { work_lat: number }).work_lat
            : null,
          workLng: typeof (u as { work_lng?: number | null }).work_lng === 'number'
            ? (u as { work_lng: number }).work_lng
            : null,
          isAgent: u.is_agent || false,
          agentType: u.agent_type ?? undefined,
          llmModel: u.llm_model ?? undefined,
          agentInstructions: u.agent_instructions ?? undefined,
          agentCurrentPeriodTokensUsed: u.agent_current_period_tokens_used || 0,
          agentTokenLimitPerPeriod: u.agent_token_limit_per_period || 100000,
          agentLastTokenResetDate: u.agent_last_token_reset_date ?? undefined,
          agentLastTokenWarningSent: u.agent_last_token_warning_sent ?? undefined,
          agentUserObservations: u.agent_user_observations ?? undefined,
          agentCapabilities: u.agent_capabilities ?? undefined,
          externalAgentSource: u.external_agent_source ?? undefined,
          teambotsAgentId: u.teambots_agent_id ?? undefined,
          teambotsOrgId: u.teambots_org_id ?? undefined,
          teambotsJobTitle: u.teambots_job_title ?? undefined,
          teambotsJobFunction: u.teambots_job_function ?? undefined,
          teambotsDepartment: u.teambots_department ?? undefined,
          teambotsProfileName: u.teambots_profile_name ?? undefined,
          teambotsStarterProfileSlug: u.teambots_starter_profile_slug ?? undefined,
          teambotsOrganizationName: u.teambots_organization_name ?? undefined,
          teambotsDefaultBrainId: u.teambots_default_brain_id ?? undefined,
          teambotsDefaultBrainDisplayName: u.teambots_default_brain_display_name ?? undefined,
          teambotsGatewayMode: (u.teambots_gateway_mode === 'mock' || u.teambots_gateway_mode === 'live')
            ? u.teambots_gateway_mode
            : undefined,
          teambotsRuntimeConfigVersion: u.teambots_runtime_config_version ?? undefined,
          teambotsManifestSyncedAt: u.teambots_manifest_synced_at ?? undefined,
          teambotsDesktopSessionUrl: u.teambots_desktop_session_url ?? undefined,
          teambotsPartnerAgentRoutingMode: u.teambots_partner_agent_routing_mode ?? undefined,
          bio: u.bio ?? undefined,
          casualName: u.casual_name ?? undefined,
          skills: u.skills ?? undefined,
          funFact: u.fun_fact ?? undefined,
          pronouns: u.pronouns ?? undefined,
          timezone: u.timezone ?? null,
          officeStatus: (u.office_status as 'in_office' | 'out_of_office' | 'out_sick' | 'on_vacation') ?? null,
          lastSeenAt: u.last_seen_at ?? null,
          // November 22, 2025: Guest account fields
          // June 2026: Integrated agents must not be treated as guests. A DB
          // trigger auto-tags any non-company email domain as a guest, and
          // agents have synthetic emails, so they get mis-flagged. Suppress it
          // for agents in the app (frontend-only safety net). Revisit when
          // third-party agents can be invited as real guests with real emails.
          isGuest: u.is_agent ? false : (membership?.is_guest || false),
          guestAccessLevel: u.is_agent ? null : (membership?.guest_access_level as 'team' | 'company' | null ?? null),
        } as User;
      });

      // CRITICAL FIX: Only update allUsers if data actually changed
      const currentAllUsers = get().allUsers;
      const hasChanged = 
        currentAllUsers.length !== combinedUsers.length ||
        combinedUsers.some((newUser) => {
          const oldUser = currentAllUsers.find(u => u.id === newUser.id);
          if (!oldUser) return true;
          
          // Compare all fields except lastSeenAt (which changes frequently)
          return (
            oldUser.name !== newUser.name ||
            oldUser.avatar !== newUser.avatar ||
            oldUser.accessRole !== newUser.accessRole ||
            oldUser.inFlowState !== newUser.inFlowState ||
            oldUser.currentActivityType !== newUser.currentActivityType ||
            oldUser.currentFocusLabel !== newUser.currentFocusLabel ||
            oldUser.officeStatus !== newUser.officeStatus ||
            oldUser.isAgent !== newUser.isAgent ||
            oldUser.team !== newUser.team ||
            JSON.stringify(oldUser.privateTeams) !== JSON.stringify(newUser.privateTeams)
          );
        });

      if (hasChanged) {
        console.log('🔄 allUsers data changed, updating array');
        
        // December 5, 2025: CRITICAL FIX for presence disappearing
        // When replacing allUsers, preserve existing lastSeenAt values if they're fresher
        // than the database values. This prevents presence dots from going dark when
        // fetchAllUsers is called, since DB updates only happen every 30 seconds.
        const currentAllUsers = get().allUsers;
        const mergedUsers = combinedUsers.map(newUser => {
          const existingUser = currentAllUsers.find(u => u.id === newUser.id);
          if (existingUser?.lastSeenAt && newUser.lastSeenAt) {
            // Keep the fresher timestamp
            const existingTime = new Date(existingUser.lastSeenAt).getTime();
            const newTime = new Date(newUser.lastSeenAt).getTime();
            if (existingTime > newTime) {
              return { ...newUser, lastSeenAt: existingUser.lastSeenAt };
            }
          } else if (existingUser?.lastSeenAt && !newUser.lastSeenAt) {
            // Preserve existing if DB has null
            return { ...newUser, lastSeenAt: existingUser.lastSeenAt };
          }
          return newUser;
        });
        
        set({ allUsers: mergedUsers });
      } else {
        console.log('✅ allUsers data unchanged, skipping update');
      }
      
      // CRITICAL FIX: Also update currentUser if they're in the list
      // This ensures Header activity button updates when activity changes.
      // Merge onto the session profile so auth-only fields (dark mode, guest,
      // invite, backplane latch, etc.) are not wiped by the directory select.
      const state = get();
      if (state.currentUser) {
        const updatedCurrentUser = combinedUsers.find(u => u.id === state.currentUser!.id);
        if (updatedCurrentUser) {
          set({
            currentUser: {
              ...state.currentUser,
              ...updatedCurrentUser,
              // Directory select now includes the latch; keep session-only fields.
              isDarkMode: state.currentUser.isDarkMode,
              inviteAccepted: state.currentUser.inviteAccepted,
              companyDomain: state.currentUser.companyDomain,
              companyName: state.currentUser.companyName,
            },
          });
          console.log('✅ Updated currentUser with fresh data (activity type:', updatedCurrentUser.currentActivityType, ')');
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      set({ isLoadingUsers: false });
    }
  },

  initializeAuth: async () => {
    console.log('userStore: initializeAuth - Starting...');
    set({ isLoadingAuth: true, coreDataReady: false });

    // Declared outside the try so the catch block can recover with the
    // already-authenticated user (Safari Private module-load failures).
    let currentUser: User | null = null;

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      console.log(
        'userStore: initializeAuth - getSession completed. Session:',
        session,
        'Error:',
        sessionError
      );

      if (sessionError) {
        console.error('userStore: initializeAuth - Error getting session:', sessionError);
        set({ isLoadingAuth: false, currentUser: null, isAuthenticated: false, coreDataReady: false });
        return { dbUser: null };
      }

      if (!session?.user) {
        set({ isLoadingAuth: false, currentUser: null, isAuthenticated: false, coreDataReady: false });
        return { dbUser: null };
      }

      console.log('userStore: initializeAuth - Calling initializeUser...');
      const { dbUser: rawDbUser } = await initializeUser();
      console.log('userStore: initializeAuth - initializeUser completed. initResult:', rawDbUser);

      if (!rawDbUser) {
        set({ isLoadingAuth: false, currentUser: null, isAuthenticated: false, coreDataReady: false });
        return { dbUser: null };
      }

      let membership: MembershipSnapshot | null = null;
      let stableCompanyId: string | null = rawDbUser.companyId ?? null;

      try {
        const { data: membershipRow, error: membershipErr } = await supabase
          .from('memberships')
          .select(`
            company_id, 
            invite_accepted, 
            is_guest, 
            guest_access_level,
            deactivated_at,
            companies (
              domain
            )
          `)
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (membershipErr) {
          console.warn('userStore: initializeAuth - membership lookup warning:', membershipErr);
        } else if (membershipRow) {
          membership = membershipRow as MembershipSnapshot;
          if (authBootstrapDecision({
            deactivated: !!membership.deactivated_at,
            inviteAccepted: !!membership.invite_accepted,
          }) === 'sign_out_deactivated') {
            console.log('userStore: initializeAuth - User is DEACTIVATED. Signing out...');
            await unsubscribeThisDevicePush().catch(() => undefined);
            await supabase.auth.signOut();
            set(clearedAuthSlice());
            alert('Your account has been deactivated. Please contact your administrator.');
            return { dbUser: null };
          }

          // Membership is source of truth; users table has no company_id.
          stableCompanyId = membership.company_id ?? stableCompanyId;
          console.log('userStore: initializeAuth - membership snapshot:', membershipRow);
        }
      } catch (e) {
        console.warn('userStore: initializeAuth - membership lookup threw:', e);
      }

      // Race: user logs in right after setting password, invite_accepted still false.
      if (membership && !membership.invite_accepted && stableCompanyId) {
        console.log('userStore: initializeAuth - Invite not accepted. Attempting self-healing update...');
        const { error: updateError } = await supabase.functions.invoke('update-invite-status', {
          body: { user_id: session.user.id, company_id: stableCompanyId },
        });

        if (updateError) {
          console.error('userStore: initializeAuth - Self-healing update failed:', updateError);
        } else {
          const { data: refetchedMembershipRow, error: refetchMembershipErr } = await supabase
            .from('memberships')
            .select(`
              company_id, 
              invite_accepted,
              is_guest,
              guest_access_level,
              companies (
                domain
              )
            `)
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (refetchMembershipErr) {
            console.warn('userStore: initializeAuth - refetch membership warning:', refetchMembershipErr);
          } else if (refetchedMembershipRow) {
            membership = refetchedMembershipRow as MembershipSnapshot;
            stableCompanyId = membership.company_id ?? stableCompanyId;
          }
        }
      }

      currentUser = composeCurrentUser({
        raw: { ...rawDbUser, companyId: stableCompanyId ?? rawDbUser.companyId ?? null },
        sessionEmail: session.user.email,
        membership,
      });

      // Desk latch: land already on the backplane before the shell paints (no exit flash).
      if (currentUser.backplanePinEnabled && currentUser.backplaneDeskCleared) {
        const { default: useBackplaneStore } = await import('../features/stream/backplaneStore');
        useBackplaneStore.getState().setPeeking(true, { animate: false });
      }

      set({
        currentUser,
        isAuthenticated: true,
        isLoadingAuth: false,
        coreDataReady: false,
      });
      console.log(
        'userStore: initializeAuth - User state set. isAuthenticated:',
        get().isAuthenticated,
        'isLoadingAuth:',
        get().isLoadingAuth
      );

      if (authBootstrapDecision({
        deactivated: false,
        inviteAccepted: !!currentUser.inviteAccepted,
      }) === 'skip_company_data') {
        console.log('userStore: initializeAuth - Invite not accepted; skipping data initialization.');
        set({ coreDataReady: true });
        return { dbUser: currentUser };
      }

      const useCompanyStore = (await import('./companyStore')).default;

      const teamStore = useTeamStore.getState();
      // Dynamically import projectStore to avoid circular dependency
      const useProjectStoreDynamic = (await import('./projectStore')).default;
      const projectStore = useProjectStoreDynamic.getState();
      const cardStore = useCardStore.getState();
      // August 13, 2025 8:45 PM: Dynamically import useSprintStore to break circular dependency
      const useSprintStoreDynamic = (await import('./sprintStore')).default;
      const sprintStore = useSprintStoreDynamic.getState();
      const roadmapStore = useRoadmapStore.getState();
      const commentStore = useCommentStore.getState();
      const followStore = useFollowStore.getState();
      const requirementStore = useRequirementStore.getState();
      const feedStore = useFeedStore.getState();
      const companyStore = useCompanyStore.getState();
      const planStore = usePlanStore.getState();
      const sharedItemStore = useSharedItemStore.getState(); // December 9, 2025: Load shared items on init

      // ============================================
      // PERFORMANCE OPTIMIZATION: Parallel Data Loading
      // ============================================
      // Strategy: Load critical data first (needed for UI), then load non-critical
      // data in parallel. This allows the app to render faster while background
      // data loads. This pattern should be followed for all new features.
      //
      // Critical (load first, sequentially if dependencies exist):
      // - Company settings (needed for UI theming/branding)
      // - Teams (needed for team selectors across app)
      // - Feed events (needed for Feed page - most common landing page)
      // - Users (needed for avatars, mentions, etc.)
      //
      // Non-critical (load in parallel after critical):
      // - Cards, Sprints, Roadmaps, Requirements, Follows, Projects
      // These can load in background while user interacts with Feed/Dashboard
      // ============================================

      // PHASE 1: Load critical data sequentially (dependencies matter)
      if (currentUser.companyId) {
        await companyStore.initializeCompanySettings(currentUser.companyId);
      }

      await teamStore.initializeTeams();
      
      // Load feed events early - Feed is the default landing page
      // This allows Feed to render immediately while other data loads
      await feedStore.initializeEvents();

      // Load users early - needed for avatars, mentions, user lookups
      console.log('userStore: initializeAuth - Fetching all users...');
      await get().fetchAllUsers(currentUser.companyId);
      console.log('userStore: initializeAuth - fetchAllUsers completed.');

      // Setup realtime subscription for users (needed for live updates)
      await get().setupUsersRealtimeSubscription();
      // Core bootstrap complete: app can render authenticated routes consistently.
      set({ coreDataReady: true });

      // PHASE 2: Load non-critical data in parallel (no dependencies between them)
      // This allows the app to be interactive while background data loads
      console.log('userStore: initializeAuth - Loading background data in parallel...');
      
      // Initialize projects BEFORE cards so ticket IDs can display correctly
      // (This is a dependency, so we keep it sequential)
      await projectStore.initializeProjects();
      
      // Now load everything else in parallel - these don't depend on each other
      await Promise.all([
        cardStore.initializeCards(),
        sprintStore.initializeSprints(),
        roadmapStore.initializeRoadmaps(),
        followStore.initializeFollows(),
        requirementStore.initializeRequirements(),
        commentStore.setupRealtimeSubscription(),
        planStore.refreshPlansIfStale(), // Initialize plans
        sharedItemStore.initializeSharedItems(null), // December 9, 2025: Load shared items for media display
      ]);

      console.log('userStore: initializeAuth - Background data loading completed. Auth initialization finished successfully.');

      set({ hasInitializedAuth: true }); // August 13, 2025 11:40 AM: Set flag after successful initialization
      
      // December 5, 2025: Setup WebSocket presence (replaces polling!)
      await get().setupPresenceChannel();

      return { dbUser: currentUser };
    } catch (error) {
      console.error('userStore: initializeAuth - Error during auth initialization:', error);
      
      // Check if this is a module loading error (Safari Private/Incognito issue)
      // Safari Private mode can block dynamic module imports, causing "Importing a module script failed"
      const isModuleError = error instanceof TypeError && 
                            String(error.message).includes('Importing a module script failed');
      
      if (isModuleError && currentUser) {
        // Module loading failed (likely Safari Private), but user IS authenticated
        // Don't reset auth - just stop the loading spinner and let them use the app
        console.warn('⚠️ Module loading failed (likely Safari Private mode), but user is authenticated. Continuing with limited functionality.');
        set({ 
          isLoadingAuth: false,
          coreDataReady: true,
          hasInitializedAuth: true, // Mark as initialized to prevent retry loops
        });
        return { dbUser: currentUser }; // Return the authenticated user
      } else {
        // Actual auth error (not module loading) - reset everything
        console.error('❌ Authentication failed - resetting auth state');
        set({
          isLoadingAuth: false,
          currentUser: null,
          isAuthenticated: false,
          coreDataReady: false,
        });
        return { dbUser: null };
      }
    }
  },

  signOut: async () => {
    // Drop the desk latch while the session is still valid so the next login is normal work.
    try {
      const { releaseBackplaneDesk } = await import('../features/stream/backplanePin');
      await releaseBackplaneDesk();
    } catch {
      /* RPC may be unavailable on older DBs */
    }
    await get().sessionLost({ redirect: false });
    await supabase.auth.signOut().catch(() => undefined);
    if (typeof window !== 'undefined' && shouldRedirectToLogin(window.location.pathname)) {
      window.location.href = '/login';
    }
  },

  sessionLost: async (opts) => {
    const redirect = opts?.redirect !== false;
    const alreadyGone = !get().currentUser && !get().isAuthenticated;
    if (!alreadyGone) {
      get().cleanupPresenceChannel();
      get().cleanupUsersRealtimeSubscription();
      await unsubscribeThisDevicePush().catch(() => undefined);
      try {
        useFeedStore.getState().teardownRealtime();
      } catch {
        /* feed store may not be ready */
      }
      try {
        const { default: useStreamStore } = await import('./streamStore');
        useStreamStore.getState().stopRealtime();
      } catch {
        /* ignore */
      }
      try {
        useCardStore.getState().cleanupRealtimeSubscription();
      } catch {
        /* ignore */
      }
      set(clearedAuthSlice());
    }
    if (redirect && typeof window !== 'undefined' && shouldRedirectToLogin(window.location.pathname)) {
      window.location.href = '/login';
    }
  },

  /**
   * Setup realtime subscription for users table updates
   */
  setupUsersRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupUsersRealtimeSubscription();

    const currentUser = get().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupUsersRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    const channel = supabase
      .channel('users_realtime_channel')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        async (payload) => {
          const updatedUser = payload.new as {
            id: string;
            office_status?: 'in_office' | 'out_of_office' | 'out_sick' | 'on_vacation' | null;
            timezone?: string | null;
            last_seen_at?: string | null;
          };
          
          // Find the existing user to compare
          const state = get();
          const existingUser = state.allUsers.find(u => u.id === updatedUser.id);
          if (!existingUser) {
            console.log('⚠️ Real-time user update: User not in allUsers list', updatedUser.id);
            return; // User not in our list
          }
          
          // Check if anything meaningful changed (not just lastSeenAt timestamp)
          const newOfficeStatus = (updatedUser.office_status as 'in_office' | 'out_of_office' | 'out_sick' | 'on_vacation') ?? existingUser.officeStatus;
          const newTimezone = updatedUser.timezone ?? existingUser.timezone;
          const newLastSeenAt = updatedUser.last_seen_at ?? existingUser.lastSeenAt;
          
          // Calculate online status (5 minutes = 300000ms)
          const wasOnline = existingUser.lastSeenAt ? (Date.now() - new Date(existingUser.lastSeenAt).getTime() < 300000) : false;
          const isOnline = newLastSeenAt ? (Date.now() - new Date(newLastSeenAt).getTime() < 300000) : false;
          
          console.log('🔍 Real-time user update check:', {
            userId: updatedUser.id,
            userName: existingUser.name,
            officeStatusChanged: newOfficeStatus !== existingUser.officeStatus,
            timezoneChanged: newTimezone !== existingUser.timezone,
            onlineStatusChanged: wasOnline !== isOnline,
            wasOnline,
            isOnline,
            oldLastSeenAt: existingUser.lastSeenAt,
            newLastSeenAt: newLastSeenAt,
          });
          
          // December 4, 2025: CRITICAL FIX for presence disappearing
          // We must ALWAYS update lastSeenAt to keep presence fresh, but we only
          // trigger a full array replacement (and re-render) when status actually changes.
          // This prevents users from appearing offline when navigating.
          const statusChanged = 
            newOfficeStatus !== existingUser.officeStatus ||
            newTimezone !== existingUser.timezone ||
            wasOnline !== isOnline;
          
          if (!statusChanged) {
            // Still update lastSeenAt silently (mutate in place) to keep it fresh
            // This prevents the stale timestamp bug where users appear offline after navigation
            existingUser.lastSeenAt = newLastSeenAt;
            console.log('✅ Updated lastSeenAt silently (no re-render needed):', existingUser.name);
            return;
          }
          
          console.log('🔄 Updating allUsers array - meaningful change detected');
          
          // Something meaningful changed, update the array (triggers re-render)
          set((state) => ({
            allUsers: state.allUsers.map((user) =>
              user.id === updatedUser.id
                ? {
                    ...user,
                    officeStatus: newOfficeStatus,
                    lastSeenAt: newLastSeenAt,
                    timezone: newTimezone,
                  }
                : user
            ),
            // Also update currentUser if it's the current user
            currentUser:
              state.currentUser && state.currentUser.id === updatedUser.id
                ? {
                    ...state.currentUser,
                    officeStatus: newOfficeStatus,
                    lastSeenAt: newLastSeenAt,
                    timezone: newTimezone,
                  }
                : state.currentUser,
          }));
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Users realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Users realtime subscription error');
        }
      });

    set({ _usersRealtimeChannel: channel });
  },

  /**
   * Clean up realtime subscription for users
   */
  cleanupUsersRealtimeSubscription: () => {
    const channel = get()._usersRealtimeChannel;
    if (channel) {
      supabase.removeChannel(channel);
      set({ _usersRealtimeChannel: null });
      console.log('✅ Users realtime subscription cleaned up');
    }
  },

  startBackgroundVideo: (url, mode) => {
    set({
      playingBackgroundVideo: true,
      backgroundVideoUrl: url,
      backgroundVideoDisplayMode: mode,
    });
  },

  stopBackgroundVideo: () => {
    set({
      playingBackgroundVideo: false,
      backgroundVideoUrl: null,
      backgroundVideoDisplayMode: 'fullscreen',
    });
  },

  // Helper functions for role checking
  isCurrentUserAdmin: (): boolean => isAdminRole(get().currentUser?.accessRole),
  isCurrentUserOwner: (): boolean => isOwnerRole(get().currentUser?.accessRole),
  isCurrentUserManager: (): boolean => isManagerRole(get().currentUser?.accessRole),
  isCurrentUserReportsAccess: (): boolean => hasReportsAccess(get().currentUser),

  toggleBackgroundVideoMode: () => {
    set((state) => ({
      backgroundVideoDisplayMode:
        state.backgroundVideoDisplayMode === 'fullscreen' ? 'background' : 'fullscreen',
    }));
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // WEBSOCKET PRESENCE - December 5, 2025
  // "There can be only one (presence dot system)." - Fortune Cookie #14
  // Instant online/offline via Supabase Realtime Presence
  // ═══════════════════════════════════════════════════════════════════════════
  
  isUserOnline: (userId: string): boolean => {
    const state = get();
    // Current user is always online (they're using the app!)
    if (userId === state.currentUser?.id) return true;
    // WebSocket presence is the source of truth
    return state.onlineUserIds.has(userId);
  },
  
  setupPresenceChannel: async () => {
    const state = get();
    
    // Prevent multiple simultaneous reconnection attempts
    if (state._isReconnecting) {
      console.log('⏸️ Presence reconnection already in progress, skipping...');
      return;
    }
    
    // CRITICAL FIX: Check if channel is already successfully subscribed
    // Don't recreate if already connected (prevents infinite reconnection loop)
    if (state._presenceChannel && state._presenceChannelStatus === 'subscribed') {
      console.log('✅ Presence channel already subscribed, skipping setup');
      return;
    }
    
    // Clean up existing channel if it exists and is in error/closed state
    if (state._presenceChannel && (state._presenceChannelStatus === 'error' || state._presenceChannelStatus === 'closed')) {
      console.log('🔌 Cleaning up existing presence channel before reconnection...');
      supabase.removeChannel(state._presenceChannel);
    } else if (state._presenceChannel && state._presenceChannelStatus === 'joining') {
      // Channel is in progress - wait for it
      console.log('⏳ Presence channel is joining, waiting...');
      return;
    }
    
    // Check network status before attempting connection
    if (!navigator.onLine) {
      console.warn('📴 Network is offline, skipping presence channel setup');
      return;
    }
    
    // Clear any pending reconnection timeout
    if (state._presenceReconnectTimeout) {
      clearTimeout(state._presenceReconnectTimeout);
      set({ _presenceReconnectTimeout: null });
    }
    
    const currentUser = state.currentUser;
    if (!currentUser?.id) return;
    
    console.log('🔌 Setting up WebSocket presence channel...');
    set({ _presenceChannelStatus: 'joining' }); // Mark as joining
    
    const channel = supabase.channel(PRESENCE_CHANNEL_NAME, {
      config: { presence: { key: currentUser.id } }
    });
    
    channel
      .on('presence', { event: 'sync' }, () => {
        // Full state sync - get all online users
        const presenceState = channel.presenceState();
        const onlineIds = new Set(Object.keys(presenceState));
        set({ onlineUserIds: onlineIds, presenceTick: get().presenceTick + 1 });
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        // User came online
        set((s) => {
          const newSet = new Set(s.onlineUserIds);
          newSet.add(key);
          return { onlineUserIds: newSet, presenceTick: s.presenceTick + 1 };
        });
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        // User went offline
        set((s) => {
          const newSet = new Set(s.onlineUserIds);
          newSet.delete(key);
          return { onlineUserIds: newSet, presenceTick: s.presenceTick + 1 };
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Successfully connected - reset reconnection state
          await channel.track({ onlineAt: new Date().toISOString() });
          console.log('✅ Presence channel subscribed & tracking');
          set({ 
            _presenceChannelStatus: 'subscribed',
            _presenceReconnectAttempts: 0,
            _isReconnecting: false,
            _presenceReconnectTimeout: null
          });
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          const errorStatus = status === 'CHANNEL_ERROR' ? 'error' : 'closed';
          console.warn('⚠️ Presence channel error:', status);
          set({ _presenceChannelStatus: errorStatus });
          
          const currentState = get();
          
          // Don't reconnect if we've exceeded max attempts (prevents infinite loop)
          const MAX_RECONNECT_ATTEMPTS = 10;
          if (currentState._presenceReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error('❌ Max reconnection attempts reached. Stopping presence reconnection.');
            set({ 
              _isReconnecting: false,
              _presenceReconnectTimeout: null
            });
            return;
          }
          
          // Don't reconnect if network is offline
          if (!navigator.onLine) {
            console.warn('📴 Network is offline, will retry when online');
            set({ _isReconnecting: false });
            return;
          }
          
          // Exponential backoff: 3s, 6s, 12s, 24s, max 30s
          const attempt = currentState._presenceReconnectAttempts + 1;
          const delay = Math.min(3000 * Math.pow(2, attempt - 1), 30000);
          
          console.log(`🔄 Scheduling presence reconnection attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms...`);
          
          set({ 
            _isReconnecting: true,
            _presenceReconnectAttempts: attempt
          });
          
          const timeout = setTimeout(() => {
            const state = get();
            if (state.currentUser?.id && navigator.onLine) {
              console.log(`🔄 Attempting to reconnect presence channel (attempt ${state._presenceReconnectAttempts})...`);
              set({ _isReconnecting: false }); // Reset flag before calling setupPresenceChannel
              get().setupPresenceChannel();
            } else {
              set({ _isReconnecting: false });
            }
          }, delay);
          
          set({ _presenceReconnectTimeout: timeout });
        }
      });
    
    set({ _presenceChannel: channel });
  },
  
  cleanupPresenceChannel: () => {
    const state = get();
    const channel = state._presenceChannel;
    
    // Clear any pending reconnection timeout
    if (state._presenceReconnectTimeout) {
      clearTimeout(state._presenceReconnectTimeout);
    }
    
    if (channel) {
      console.log('🔌 Cleaning up presence channel');
      supabase.removeChannel(channel);
    }
    
    set({ 
      _presenceChannel: null,
      _presenceChannelStatus: 'idle',
      onlineUserIds: new Set(),
      _presenceReconnectTimeout: null,
      _presenceReconnectAttempts: 0,
      _isReconnecting: false
    });
  },
}));

// Debug: Log when allUsers reference changes
if (typeof window !== 'undefined') {
  let lastAllUsers = useUserStore.getState().allUsers;
  useUserStore.subscribe((state) => {
    if (state.allUsers !== lastAllUsers) {
      console.log('🔄 allUsers array reference changed!', {
        beforeLength: lastAllUsers.length,
        afterLength: state.allUsers.length,
        timestamp: new Date().toISOString(),
        stack: new Error().stack
      });
      lastAllUsers = state.allUsers;
    }
  });
}

// Tu Metrics - December 5, 2025
// See: Fortune Cookie #14 "There Can Be Only One"
Object.assign(useUserStore, { tuMetrics: {
  id: 'user-store',
  tu1: 0.80,  // Presence simplified with WebSocket
  tu2: 0.88,  // Clean separation: WebSocket for presence, DB for audit
  tu3: 8000,  // Core layer + production-ready presence
  breakageResistance: 1.6,  // WebSocket = instant, reliable presence
  notes: 'December 2025: WEBSOCKET PRESENCE via Supabase Realtime. ' +
         'Instant online/offline detection. No more polling. ' +
         'DB writes reduced from every 30s to every 5min (audit only). ' +
         '"There can be only one (presence dot system)." #14',
  lastRefactor: '2025-12-05'
}});

export default useUserStore;
